import React, { useMemo, useState } from 'react';
import { useT } from '../../i18n';

// Sortable, filterable transaction history.
//
// Deliberately close to PositionsTable in shape and idiom — same em-dash for a
// missing value, same sort behaviour, same filter chips — because the two are
// read as one view of the same book and gratuitous differences cost the reader.
//
// The list is long (the Fidelity account alone runs to several hundred rows),
// so it renders a page at a time. Filtering and sorting always run over the
// WHOLE list, not the rendered page: a search that only looked at the first 200
// rows would quietly answer the wrong question.

const COLUMNS = [
    { key: 'tradeDate', labelKey: 'activity.date', align: 'left' },
    { key: 'type', labelKey: 'activity.type', align: 'left' },
    { key: 'symbol', labelKey: 'activity.symbol', align: 'left' },
    { key: 'description', labelKey: 'activity.description', align: 'left', wide: true },
    { key: 'quantity', labelKey: 'activity.qty', align: 'right', numeric: true },
    { key: 'price', labelKey: 'activity.price', align: 'right', numeric: true },
    { key: 'amount', labelKey: 'activity.amount', align: 'right', numeric: true, signed: true },
    { key: 'fee', labelKey: 'activity.fee', align: 'right', numeric: true },
];

/**
 * Type chips, grouped by the question a reader is actually asking.
 *
 * The vocabulary is SnapTrade's normalised set. An unrecognised type still
 * shows under "All" — a filter that silently hid a transaction because the
 * parser had not met its type before would be the same class of bug as the
 * dropped positions this view exists to make visible.
 */
const GROUPS = [
    { key: 'all', labelKey: 'common.all', types: null },
    { key: 'trades', labelKey: 'activity.group.trades', types: ['BUY', 'SELL'] },
    {
        key: 'income',
        labelKey: 'activity.group.income',
        types: ['DIVIDEND', 'SUBSTITUTE_DIVIDEND', 'STOCK_DIVIDEND', 'REI', 'INTEREST'],
    },
    { key: 'transfers', labelKey: 'activity.group.transfers', types: ['CONTRIBUTION', 'WITHDRAWAL', 'TRANSFER'] },
    { key: 'fees', labelKey: 'activity.group.fees', types: ['FEE', 'TAX'] },
    {
        key: 'options',
        labelKey: 'activity.group.options',
        types: ['OPTIONEXPIRATION', 'OPTIONASSIGNMENT', 'OPTIONEXERCISE'],
    },
];

const PAGE_SIZE = 200;

function formatNumber(value, { decimals = 2 } = {}) {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value);
    return n.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
}

function cellContent(activity, column) {
    const raw = activity[column.key];
    if (column.key === 'type' && raw) {
        return <span className={`activity__type is-${raw.toLowerCase()}`}>{raw}</span>;
    }
    // Quantity is signed by the broker — negative for a sale — and fractional
    // for reinvested dividends, so it keeps its decimals rather than rounding
    // a 0.37-share reinvestment away to nothing.
    if (column.key === 'quantity') return formatNumber(raw, { decimals: 4 });
    if (column.key === 'price') return formatNumber(raw, { decimals: 4 });
    if (column.numeric) return formatNumber(raw);
    return raw || null;
}

export default function ActivityTable({ activities, currency }) {
    const { t, tn } = useT();
    const [group, setGroup] = useState('all');
    const [query, setQuery] = useState('');
    const [sort, setSort] = useState({ key: 'tradeDate', direction: 'desc' });
    const [shown, setShown] = useState(PAGE_SIZE);

    // Only offer a chip when the history actually contains that kind of row.
    const groups = useMemo(() => {
        const present = new Set(activities.map((a) => a.type).filter(Boolean));
        return GROUPS.filter((g) => !g.types || g.types.some((t) => present.has(t)));
    }, [activities]);

    const visible = useMemo(() => {
        const needle = query.trim().toLowerCase();
        const types = GROUPS.find((g) => g.key === group)?.types;

        const filtered = activities.filter((a) => {
            if (types && !types.includes(a.type)) return false;
            if (!needle) return true;
            // Descriptive fields only, as in PositionsTable: matching numbers
            // would make "2" hit almost every row through price or amount.
            return [a.symbol, a.name, a.description, a.type, a.accountLabel].some(
                (field) => field && String(field).toLowerCase().includes(needle),
            );
        });

        const column = COLUMNS.find((c) => c.key === sort.key);
        return [...filtered].sort((a, b) => {
            const av = a[sort.key];
            const bv = b[sort.key];
            if (av === null || av === undefined || av === '') return 1;
            if (bv === null || bv === undefined || bv === '') return -1;
            const cmp = column?.numeric
                ? Number(av) - Number(bv)
                : String(av).localeCompare(String(bv));
            return sort.direction === 'asc' ? cmp : -cmp;
        });
    }, [activities, group, query, sort]);

    const toggleSort = (key) => {
        setSort((current) =>
            current.key === key
                ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
                : { key, direction: COLUMNS.find((c) => c.key === key)?.numeric ? 'desc' : 'asc' },
        );
        setShown(PAGE_SIZE);
    };

    const changeGroup = (key) => {
        setGroup(key);
        setShown(PAGE_SIZE);
    };

    const changeQuery = (value) => {
        setQuery(value);
        setShown(PAGE_SIZE);
    };

    // Net cash effect of what is on screen. Buys are negative and sales
    // positive, so this answers "what did this filter do to the account".
    const net = visible.reduce((acc, a) => acc + Number(a.amount || 0), 0);
    const page = visible.slice(0, shown);

    return (
        <>
            <div className="positions__controls">
                <div className="positions__filters">
                    {groups.map((g) => (
                        <button
                            key={g.key}
                            type="button"
                            className={`positions__filter${group === g.key ? ' is-active' : ''}`}
                            onClick={() => changeGroup(g.key)}
                        >
                            {t(g.labelKey)}
                        </button>
                    ))}
                    <input
                        type="search"
                        className="positions__search"
                        placeholder={t('common.filter')}
                        value={query}
                        onChange={(e) => changeQuery(e.target.value)}
                        aria-label={t('activity.filterAria')}
                    />
                </div>
                <div className="positions__count">
                    {tn('activity.count', visible.length)}
                    {net !== 0 && (
                        <span className="positions__subtotal">
                            {' · '}
                            {t('activity.net', { amount: formatNumber(net), currency })}
                        </span>
                    )}
                </div>
            </div>

            <div className="positions__scroll">
                <table className="positions activity">
                    <thead>
                        <tr>
                            {COLUMNS.map((column) => (
                                <th
                                    key={column.key}
                                    className={`is-${column.align}${column.wide ? ' is-wide' : ''}${
                                        sort.key === column.key ? ' is-sorted' : ''
                                    }`}
                                >
                                    <button type="button" onClick={() => toggleSort(column.key)}>
                                        {t(column.labelKey)}
                                        {sort.key === column.key && (
                                            <span aria-hidden="true">
                                                {sort.direction === 'asc' ? ' ↑' : ' ↓'}
                                            </span>
                                        )}
                                    </button>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {page.map((activity, i) => (
                            // The broker's id is the key where there is one. Some
                            // rows arrive without it, so the index is the fallback
                            // rather than a composite that could still collide.
                            <tr key={activity.id ?? `row-${i}`}>
                                {COLUMNS.map((column) => {
                                    const content = cellContent(activity, column);
                                    const negative =
                                        column.signed && Number(activity[column.key]) < 0;
                                    return (
                                        <td
                                            key={column.key}
                                            className={`is-${column.align}${
                                                column.wide ? ' is-wide' : ''
                                            }${negative ? ' is-negative' : ''}${
                                                column.signed && !negative && content
                                                    ? ' is-positive'
                                                    : ''
                                            }`}
                                        >
                                            {content ?? <span className="positions__empty">—</span>}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {shown < visible.length && (
                <div className="activity__more">
                    <button type="button" onClick={() => setShown((n) => n + PAGE_SIZE)}>
                        {t('activity.showMore', { count: Math.min(PAGE_SIZE, visible.length - shown) })}
                    </button>
                    <span className="activity__more_count">
                        {t('activity.showing', { shown: page.length, total: visible.length })}
                    </span>
                </div>
            )}
        </>
    );
}
